/**
 * Fund ledger section for the sponsor drill-down (NOS-168): balance, entries
 * with running balance, record-disbursement/adjustment dialog, CSV export.
 */
import { useMemo, useState } from "react"
import { Download, Plus } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useFundLedger } from "@/hooks/useFundLedger"
import {
  ENTRY_TYPE_LABELS,
  buildLedgerCsv,
  formatSignedCurrency,
  withRunningBalance,
} from "@/lib/fundLedger"
import { downloadCsv, formatCurrencyCents } from "@/lib/sponsor"

export function FundLedgerSection({
  sponsorshipId,
  childName,
  canRecord,
}: {
  sponsorshipId: string
  childName: string
  canRecord: boolean
}) {
  const ledger = useFundLedger(sponsorshipId)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [entryType, setEntryType] = useState<"disbursement" | "adjustment">("disbursement")
  const [amount, setAmount] = useState("")
  const [memo, setMemo] = useState("")
  const [formError, setFormError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const rows = useMemo(() => withRunningBalance(ledger.entries).reverse(), [ledger.entries])

  const handleExport = () => {
    if (ledger.entries.length === 0) {
      toast.info("No ledger entries to export yet.")
      return
    }
    if (ledger.truncated) {
      toast.warning("Ledger is large — export contains the first 2,000 entries.")
    }
    downloadCsv(
      buildLedgerCsv(ledger.entries),
      `fund-ledger-${childName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.csv`
    )
  }

  const handleRecord = async (e: React.FormEvent) => {
    e.preventDefault()
    const dollars = Number.parseFloat(amount)
    if (Number.isNaN(dollars) || dollars <= 0) {
      setFormError("Enter a positive amount")
      return
    }
    if (!memo.trim()) {
      setFormError("Memo is required")
      return
    }
    setFormError(null)
    setSaving(true)
    const cents = Math.round(dollars * 100)
    // Disbursements are stored negative; adjustments here are credits —
    // negative adjustments can be entered by recording a disbursement-like
    // correction with a memo.
    const signed = entryType === "disbursement" ? -cents : cents
    const { error } = await ledger.recordEntry({
      type: entryType,
      amountCents: signed,
      memo: memo.trim(),
    })
    setSaving(false)
    if (error) {
      setFormError(error.message)
      return
    }
    toast.success(`${ENTRY_TYPE_LABELS[entryType]} recorded`)
    setDialogOpen(false)
    setAmount("")
    setMemo("")
  }

  return (
    <section>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Fund ledger</h2>
        <div className="flex gap-2">
          {canRecord && (
            <Button size="sm" variant="outline" onClick={() => setDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" aria-hidden />
              Record entry
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" aria-hidden />
            Export ledger CSV
          </Button>
        </div>
      </div>

      <Card className="mb-4 max-w-xs">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Fund balance</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-bold tabular-nums">
            {formatCurrencyCents(ledger.balanceCents)}
          </p>
          <p className="text-xs text-muted-foreground">
            Donations − admin fees − disbursements
          </p>
        </CardContent>
      </Card>

      {ledger.error && (
        <p className="mb-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {ledger.error}
        </p>
      )}

      {ledger.entries.length === 0 && !ledger.loading ? (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-gray-500">
          No ledger activity yet. Donations to this project will appear here automatically.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {["Date", "Type", "Memo", "Amount", "Balance"].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((e) => (
                <tr key={e.id}>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(e.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">{ENTRY_TYPE_LABELS[e.type]}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{e.memo ?? ""}</td>
                  <td
                    className={`px-4 py-3 tabular-nums ${e.amount < 0 ? "text-red-600" : "text-green-700"}`}
                  >
                    {formatSignedCurrency(e.amount)}
                  </td>
                  <td className="px-4 py-3 tabular-nums">
                    {formatCurrencyCents(e.runningBalanceCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Record fund entry</DialogTitle>
            <DialogDescription>
              Disbursements pay funds out to {childName} and cannot exceed the current balance.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRecord}>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="entry-type">Type</Label>
                <Select
                  value={entryType}
                  onValueChange={(v: "disbursement" | "adjustment") => setEntryType(v)}
                  disabled={saving}
                >
                  <SelectTrigger id="entry-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disbursement">Disbursement (funds out)</SelectItem>
                    <SelectItem value="adjustment">Adjustment (credit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="entry-amount">Amount ($)</Label>
                <Input
                  id="entry-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={saving}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="entry-memo">Memo</Label>
                <Input
                  id="entry-memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Q3 program funds transfer"
                  disabled={saving}
                />
              </div>
              {formError && <p className="text-sm text-destructive">{formError}</p>}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? "Recording..." : "Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  )
}
